import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardActionArea,
  Divider,
  Container,
  Tooltip,
  IconButton,
  LinearProgress,
  CardContent,
  Checkbox,
  Button,
  Chip,
  Paper,
  Stack,
  Avatar,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReaderSlider from './ReaderSlider';
import CustomMarkdown from '../../../../shared/ui/CustomMarkdown';
import { theme } from '../../../../theme';
import { BaseUrl } from '../../../../api/http';
import * as storiesApi from '../../../../api/stories';
import AlertMessage from '../../../../shared/ui/AlertMessage';
import ReaderSiteHeader from '../../../../shared/ui/ReaderSiteHeader';

type ChapterPage = {
  id: string;
  name: string;
  full: string;
  thumbnail: string;
  lastUpdate: string;
  version: number;
};

type StoryChapterItem = {
  id: string;
  title: string;
  description?: string;
  lastUpdate: string;
  readerId?: string;
};

type ReaderLocationState = {
  fromPath?: string;
  freeMode?: boolean;
  story?: {
    id: string;
    name: string;
    description: string;
    cover: string;
    chapters: StoryChapterItem[];
  };
};

interface ReaderContentProps {
  title: string;
  description: string;
  lastUpdate: string;
  pages: {
    items: ChapterPage[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  currentChapterId?: string;
  onPageClick?: (src: string) => void;
}

const ReaderContent: React.FC<ReaderContentProps> = ({
  title,
  description,
  lastUpdate,
  currentChapterId,
  pages = {
    items: [],
    total: 0,
    page: 0,
    pageSize: 10,
    totalPages: 1,
  },
  onPageClick,
}) => {
  const [sliderOpen, setSliderOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state ?? {}) as ReaderLocationState;
  const match = location.pathname.match(/\/g\/([a-f0-9-]+)/i);
  const chapterId = currentChapterId ?? match?.[1] ?? null;
  const story = state.story;
  const fromPath = state.fromPath || (state.freeMode ? '/stories-notext' : '/stories');

  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  type AlertType = 'success' | 'error' | 'info' | 'warning';
  const [alert, setAlert] = useState<{ open: boolean; type: AlertType; message: string }>({
    open: false,
    type: 'success',
    message: '',
  });
  type DownloadPhase = 'idle' | 'preparing' | 'downloading';
  const [downloadPhase, setDownloadPhase] = useState<DownloadPhase>('idle');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const isFree = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    return qs.get('free') === '1';
  }, [location.search]);

  const chapters = useMemo(() => story?.chapters ?? [], [story]);
  const currentChapterIndex = useMemo(
    () => chapters.findIndex((chapter) => String(chapter.id) === String(chapterId)),
    [chapterId, chapters]
  );
  const currentChapter = currentChapterIndex >= 0 ? chapters[currentChapterIndex] : null;
  const selectedCount = selectedIds.size;

  const goToChapter = (targetChapter: StoryChapterItem) => {
    const targetId = targetChapter.id;
    const suffix = isFree ? '?free=1' : '';
    navigate(`/g/${targetId}${suffix}`, {
      state: {
        ...state,
        fromPath,
      },
    });
  };

  useEffect(() => {
    if (pages.items?.length > 0) {
      const preload = new Image();
      preload.src = `${BaseUrl}${pages.items[0].full}?v=${pages.items[0].version}`;
    }
  }, [pages.items]);

  const handleClick = (index: number, src: string, version: number) => {
    setCurrentIndex(index);
    setSliderOpen(true);
    if (onPageClick) {
      onPageClick(`${src}?v=${version}`);
    }
  };

  function getFileNameFromContentDisposition(disposition: string): string | null {
    if (!disposition) return null;

    const utf8Match = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1].trim().replace(/^["']|["']$/g, ''));
      } catch {
        // ignore
      }
    }

    const asciiMatch = disposition.match(/filename="?([^"]+)"?/i);
    if (asciiMatch?.[1]) {
      return asciiMatch[1].trim().replace(/[/\\?%*:|"<>]/g, '_');
    }

    return null;
  }

  type ApiErrorShape = { error?: string; message?: string; statusCode?: number; exceptionType?: string };

  async function getAxiosErrorMessage(err: unknown): Promise<string> {
    if (!axios.isAxiosError(err)) return 'A server error occurred. Please try again later.';

    let data: any = err.response?.data;

    if (data instanceof Blob) {
      try {
        const text = await data.text();
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }
      } catch {
        // ignore
      }
    } else if (data instanceof ArrayBuffer) {
      try {
        const text = new TextDecoder().decode(data);
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }
      } catch {
        // ignore
      }
    }

    if (typeof data === 'string' && data.trim().startsWith('{')) {
      try {
        data = JSON.parse(data);
      } catch {
        // ignore
      }
    }

    let message: string | undefined;
    if (data && typeof data === 'object') {
      const objectData = data as ApiErrorShape;
      message = objectData.error || objectData.message;
    } else if (typeof data === 'string') {
      const matchText =
        data.match(/"error"\s*:\s*"([^"]+)"/i) || data.match(/"message"\s*:\s*"([^"]+)"/i);
      message = matchText?.[1] ?? data;
    }

    const status = err.response?.status ?? (data as ApiErrorShape | undefined)?.statusCode;
    if (status && status !== 500 && message) return message.trim();
    return message?.trim() || err.response?.statusText || 'A server error occurred. Please try again later.';
  }

  const handleDownload = async () => {
    setDownloading(true);
    setProgress(0);

    try {
      const payload = {
        chapterId: chapterId as string,
        pages: Array.from(selectedIds),
      };

      setDownloadPhase('preparing');

      const onProg = (evt: ProgressEvent) => {
        if (downloadPhase !== 'downloading') setDownloadPhase('downloading');

        if (evt.lengthComputable && evt.total > 0) {
          const percent = Math.round((evt.loaded * 100) / evt.total);
          setProgress(percent);
        } else {
          const approx = Math.min(99, Math.max(1, Math.floor(evt.loaded / 100000)));
          setProgress(approx);
        }
      };

      const response = await (
        isFree
          ? storiesApi.getDownloadFreeLinkByChapterId(payload, onProg)
          : storiesApi.getDownloadLinkByChapterId(payload, onProg)
      );

      const disposition = (response.headers?.['content-disposition'] ?? '') as string;
      const fileName = getFileNameFromContentDisposition(disposition) || `chapter-${chapterId}.zip`;

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);

      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) {
      const msg = await getAxiosErrorMessage(err);
      setAlert({ open: true, type: 'error', message: msg });
    } finally {
      setDownloading(false);
      setDownloadPhase('idle');
      setProgress(0);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const chapterDescription = currentChapter?.description || description;

  return (
    <Box sx={{ backgroundColor: theme.palette.custom.readerBg, py: 4 }}>
      <ReaderSiteHeader
        mode="reader"
        title={story?.name || title}
        subtitle={isFree ? 'Reading the free chapter view.' : 'Reading the full chapter view.'}
      />

      <AlertMessage
        open={alert.open}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
      />

      <Container maxWidth="lg">
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            mb: 3,
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            background:
              'radial-gradient(circle at top left, rgba(209, 76, 255, 0.16), transparent 30%), linear-gradient(180deg, rgba(42, 36, 48, 0.96), rgba(28, 24, 33, 0.96))',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.22)',
          }}
        >
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                  <Chip icon={<MenuBookOutlinedIcon />} label={isFree ? 'Free chapter' : 'Full access'} color="primary" />
                  <Chip icon={<CollectionsOutlinedIcon />} label={`${pages.items.length} scenes`} variant="outlined" />
                  {selectedCount > 0 && <Chip label={`${selectedCount} selected`} variant="outlined" />}
                </Stack>
                <Typography variant="h3" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 800 }}>
                  {title}
                </Typography>
                <Typography sx={{ color: theme.palette.text.secondary, maxWidth: 720 }}>
                  {chapterDescription || 'Open scenes, switch chapters, and stay in reading mode without losing context.'}
                </Typography>
              </Stack>

              <Stack direction={{ xs: 'row', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap" alignItems="flex-start">
                <Button variant="outlined" startIcon={<ArrowBackOutlinedIcon />} onClick={() => navigate(fromPath)}>
                  Back to library
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SlideshowOutlinedIcon />}
                  onClick={() => pages.items.length > 0 && handleClick(0, pages.items[0].full, pages.items[0].version)}
                  disabled={pages.items.length === 0}
                >
                  Open slideshow
                </Button>
                <Tooltip title="Download selected images as ZIP">
                  <span>
                    <IconButton
                      onClick={handleDownload}
                      size="large"
                      sx={{
                        color: theme.palette.custom.readerHeader,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 3,
                      }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>

            {downloading && (
              <Box sx={{ width: '100%' }}>
                <Typography variant="caption" sx={{ color: theme.palette.custom.readerLastUpdateText }}>
                  {downloadPhase === 'preparing'
                    ? 'Preparing files for download...'
                    : `Downloading... ${Number.isFinite(progress) ? progress : 0}%`}
                </Typography>

                <LinearProgress
                  variant={downloadPhase === 'preparing' ? 'indeterminate' : 'determinate'}
                  value={downloadPhase === 'downloading' ? progress : undefined}
                  sx={{
                    mt: 0.75,
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: theme.palette.custom.readerScrollTrack,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: theme.palette.primary.main,
                    },
                  }}
                />
              </Box>
            )}
          </Stack>
        </Paper>

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="flex-start">
          <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
            <Paper
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 4,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h5" sx={{ color: theme.palette.custom.readerHeader, mb: 0.5 }}>
                    Chapter navigator
                  </Typography>
                  <Typography sx={{ color: theme.palette.text.secondary }}>
                    Move between chapters without leaving the reader.
                  </Typography>
                </Box>

                {chapters.length > 1 && currentChapterIndex >= 0 && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={<ArrowBackOutlinedIcon />}
                      disabled={currentChapterIndex <= 0}
                      onClick={() => goToChapter(chapters[currentChapterIndex - 1])}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardOutlinedIcon />}
                      disabled={currentChapterIndex >= chapters.length - 1}
                      onClick={() => goToChapter(chapters[currentChapterIndex + 1])}
                    >
                      Next
                    </Button>
                  </Stack>
                )}
              </Stack>

              {chapters.length > 0 ? (
                <Stack spacing={2}>
                  {currentChapter && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.primary.main}`,
                        background: 'linear-gradient(180deg, rgba(209, 76, 255, 0.16), rgba(209, 76, 255, 0.04))',
                      }}
                    >
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: theme.palette.primary.main,
                            color: '#fff',
                            fontSize: 13,
                            fontWeight: 700,
                          }}
                        >
                          {currentChapterIndex + 1}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 0.5 }}>
                            <Chip size="small" color="primary" label="Current" />
                            {(() => {
                              const updatedAt = new Date(currentChapter.lastUpdate);
                              const diffMs = Date.now() - updatedAt.getTime();
                              const diffDays = diffMs / (1000 * 60 * 60 * 24);
                              return diffDays <= 7 ? <Chip size="small" variant="outlined" label="New" /> : null;
                            })()}
                          </Stack>
                          <Typography sx={{ fontWeight: 700 }}>{currentChapter.title}</Typography>
                          <Typography sx={{ color: theme.palette.text.secondary, fontSize: 13 }}>
                            {new Date(currentChapter.lastUpdate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  )}

                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{
                      maxHeight: 120,
                      overflowY: 'auto',
                      pr: 0.5,
                    }}
                  >
                    {chapters.map((chapter, index) => {
                      const selected = String(chapter.id) === String(chapterId);
                      return (
                        <Chip
                          key={chapter.id}
                          label={`${index + 1}. ${chapter.title}`}
                          color={selected ? 'primary' : 'default'}
                          variant={selected ? 'filled' : 'outlined'}
                          onClick={() => goToChapter(chapter)}
                          sx={{ maxWidth: '100%' }}
                        />
                      );
                    })}
                  </Stack>

                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.divider}`,
                      background: 'rgba(255,255,255,0.02)',
                      maxHeight: 220,
                      overflowY: 'auto',
                    }}
                  >
                    <List disablePadding>
                      {chapters.map((chapter, index) => {
                        const selected = String(chapter.id) === String(chapterId);
                        return (
                          <ListItemButton
                            key={chapter.id}
                            onClick={() => goToChapter(chapter)}
                            selected={selected}
                            sx={{
                              px: 2,
                              py: 1,
                              alignItems: 'flex-start',
                              borderLeft: selected ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                            }}
                          >
                            <ListItemText
                              primary={
                                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                                    {index + 1}. {chapter.title}
                                  </Typography>
                                  {selected && <Chip size="small" color="primary" label="Current" />}
                                </Stack>
                              }
                              secondary={
                                <Typography sx={{ color: theme.palette.text.secondary, fontSize: 12, mt: 0.5 }}>
                                  {new Date(chapter.lastUpdate).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Paper>
                </Stack>
              ) : (
                <Typography sx={{ color: theme.palette.text.secondary }}>
                  Chapter navigation is not available for this item yet.
                </Typography>
              )}
            </Paper>

            <Paper
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 4,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Typography variant="h5" sx={{ color: theme.palette.custom.readerHeader, mb: 2 }}>
                Chapter details
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ color: theme.palette.custom.readerTitle, fontWeight: 'bold', mb: 1 }}>
                  {title}
                </Typography>
                <Box
                  sx={{
                    backgroundColor: theme.palette.custom.readerDescriptionBg,
                    p: 2,
                    borderRadius: 2,
                    mb: 1,
                  }}
                >
                  <CustomMarkdown>{description}</CustomMarkdown>
                </Box>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: theme.palette.custom.readerLastUpdateBg,
                    p: 1.5,
                    borderRadius: 2,
                    mb: 2,
                  }}
                >
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: theme.palette.custom.readerLastUpdateText }} />
                  <Typography variant="caption" sx={{ color: theme.palette.custom.readerLastUpdateText }}>
                    Last update: {lastUpdate}
                  </Typography>
                </Box>
                <Divider sx={{ mt: 2, backgroundColor: theme.palette.custom.readerDivider }} />
              </Box>

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                <Chip icon={<CollectionsOutlinedIcon />} label={`${pages.items.length} scenes available`} color="primary" />
                <Chip label={`${selectedCount} selected for download`} variant="outlined" />
              </Stack>

              <Box
                sx={{
                  maxHeight: 520,
                  overflowY: 'auto',
                  pr: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2.5,
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  scrollbarWidth: 'thin',
                  scrollbarColor: `${theme.palette.custom.readerScrollThumb} ${theme.palette.custom.readerScrollTrack}`,
                  '&::-webkit-scrollbar': {
                    width: '10px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: theme.palette.custom.readerScrollTrack,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.custom.readerScrollThumb,
                    borderRadius: 0,
                    border: `2px solid ${theme.palette.custom.readerScrollTrack}`,
                  },
                  '&::-webkit-scrollbar-button': {
                    display: 'none',
                  },
                }}
              >
                {(pages?.items ?? []).map((page, index) => {
                  const isRecent = (() => {
                    if (!page.lastUpdate) return false;
                    const updatedAt = new Date(page.lastUpdate);
                    const now = new Date();
                    const diffMs = now.getTime() - updatedAt.getTime();
                    const diffDays = diffMs / (1000 * 60 * 60 * 24);
                    return diffDays <= 3;
                  })();

                  return (
                    <Card
                      key={page.id}
                      sx={{
                        width: 188,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.custom.readerThumbBorder}`,
                        position: 'relative',
                        overflow: 'hidden',
                        backgroundColor: 'rgba(255,255,255,0.02)',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          inset: 0,
                          pointerEvents: 'none',
                          borderRadius: 'inherit',
                          transition: 'box-shadow .15s ease',
                        },
                        '&:hover::after': {
                          boxShadow: `0 0 0 2px ${theme.palette.primary.main} inset`,
                        },
                      }}
                    >
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          bgcolor: theme.palette.background.paper,
                          borderRadius: '9999px',
                          boxShadow: 3,
                          p: 0.2,
                          zIndex: 2,
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={selectedIds.has(page.id)}
                          onChange={() => toggleSelect(page.id)}
                          inputProps={{ 'aria-label': `Select ${page.name}` }}
                        />
                      </Box>
                      <CardActionArea onClick={() => handleClick(index, page.full, page.version)}>
                        <CardMedia
                          component="img"
                          image={`${BaseUrl}${page.thumbnail}?v=${page.version}`}
                          alt={page.name}
                          loading="lazy"
                          sx={{
                            objectFit: 'cover',
                            width: '100%',
                            height: '220px',
                            display: 'block',
                            filter: isRecent ? 'blur(12px)' : 'none',
                            transition: 'filter 0.3s ease',
                          }}
                        />
                      </CardActionArea>
                      {isRecent && (
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            backgroundColor: 'rgba(0,0,0,0.3)',
                            zIndex: 1,
                            pointerEvents: 'none',
                            userSelect: 'none',
                          }}
                        >
                          NEW
                        </Box>
                      )}
                      <CardContent sx={{ py: 1.25, px: 1.25 }}>
                        <Tooltip title={page.name}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: theme.palette.custom.readerHeader,
                              textAlign: 'center',
                              fontWeight: 700,
                              lineHeight: 1.2,
                              mt: 0.5,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {page.name}
                          </Typography>
                        </Tooltip>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Paper>
          </Stack>

          <Paper
            sx={{
              width: { xs: '100%', xl: 320 },
              p: 2,
              position: { xs: 'static', xl: 'sticky' },
              top: 108,
              borderRadius: 4,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Reading panel
            </Typography>
            <Stack spacing={1.5}>
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, background: 'rgba(209, 76, 255, 0.08)' }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Current chapter
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
              </Paper>
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, background: 'rgba(255,255,255,0.03)' }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Library source
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>{isFree ? 'No Text' : 'Stories'}</Typography>
              </Paper>
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, background: 'rgba(255,255,255,0.03)' }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Quick stats
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>{pages.items.length} total scenes</Typography>
                <Typography sx={{ color: theme.palette.text.secondary, fontSize: 13 }}>
                  {selectedCount > 0 ? `${selectedCount} selected for download` : 'Select scenes to build a ZIP download.'}
                </Typography>
              </Paper>
            </Stack>
          </Paper>
        </Stack>

        {sliderOpen && (
          <ReaderSlider
            images={(pages?.items ?? []).map((page) => `${BaseUrl}${page.full}?v=${page.version}`)}
            initialIndex={currentIndex}
            onClose={() => setSliderOpen(false)}
          />
        )}
      </Container>
    </Box>
  );
};

export default ReaderContent;
